import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { billingMe } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { X, Clock, Sparkles } from "lucide-react";

const DISMISS_KEY_PREFIX = "atelier.trialBanner.dismissed_until.";

export default function TrialBanner() {
  const { user } = useAuth();
  const loc = useLocation();
  const [billing, setBilling] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const dismissKey = user?.id ? DISMISS_KEY_PREFIX + user.id : null;

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    if (user.role === "admin") return;
    billingMe().then(setBilling).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!dismissKey) {
      setDismissed(false);
      return;
    }
    try {
      const until = Number(localStorage.getItem(dismissKey) || 0);
      setDismissed(!!(until && Date.now() < until));
    } catch {
      /* noop */
    }
  }, [dismissKey]);

  if (!user || typeof user !== "object") return null;
  if (user.role === "admin") return null;
  if (!billing) return null;
  if (loc.pathname.startsWith("/profile") || loc.pathname.startsWith("/billing"))
    return null;
  if (dismissed) return null;

  const status = billing.subscription_status;
  const daysLeft = billing.trial_days_left;

  // Active subscriber → no banner
  if (status === "active" && !billing.cancel_at_period_end) return null;

  function dismiss() {
    try {
      // Dismiss for 24 hours, per-user
      if (dismissKey) {
        localStorage.setItem(
          dismissKey,
          String(Date.now() + 24 * 60 * 60 * 1000)
        );
      }
    } catch {
      /* noop */
    }
    setDismissed(true);
  }

  // Trial expired or canceled → red
  const expired =
    status === "canceled" ||
    (status === "trialing" && daysLeft === 0) ||
    (status !== "active" && status !== "trialing" && !billing.has_access);

  if (expired) {
    return (
      <div
        className="bg-red-50 border-b border-red-200 px-6 py-2.5"
        data-testid="trial-banner-expired"
      >
        <div className="max-w-[1600px] mx-auto flex items-center gap-3 text-sm">
          <Clock className="w-4 h-4 text-red-700" strokeWidth={1.5} />
          <span className="text-red-900 font-medium">
            Your free trial has ended.
          </span>
          <span className="text-red-700 hidden sm:inline">
            Subscribe to keep your studio running.
          </span>
          <Link
            to="/profile"
            className="ml-auto bg-red-700 hover:bg-red-800 text-white text-xs font-semibold uppercase tracking-wider px-4 py-1.5 rounded-full transition-colors"
            data-testid="trial-banner-subscribe"
          >
            Subscribe now
          </Link>
        </div>
      </div>
    );
  }

  if (status === "trialing" && typeof daysLeft === "number") {
    const urgent = daysLeft <= 7;
    return (
      <div
        className={
          "border-b px-6 py-2.5 " +
          (urgent
            ? "bg-amber-50 border-amber-200"
            : "bg-stone-50 border-stone-200")
        }
        data-testid="trial-banner"
      >
        <div className="max-w-[1600px] mx-auto flex items-center gap-3 text-sm">
          <Sparkles
            className={
              "w-4 h-4 " + (urgent ? "text-amber-700" : "text-stone-600")
            }
            strokeWidth={1.5}
          />
          <span
            className={urgent ? "text-amber-900 font-medium" : "text-stone-800"}
            data-testid="trial-banner-text"
          >
            {daysLeft === 0
              ? "Your free trial ends today."
              : `Free trial: ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
          </span>
          <Link
            to="/profile"
            className={
              "ml-auto text-xs font-semibold uppercase tracking-wider px-4 py-1.5 rounded-full transition-colors " +
              (urgent
                ? "bg-amber-700 hover:bg-amber-800 text-white"
                : "bg-stone-900 hover:bg-stone-800 text-white")
            }
            data-testid="trial-banner-upgrade"
          >
            Upgrade
          </Link>
          <button
            onClick={dismiss}
            data-testid="trial-banner-dismiss"
            className="text-stone-500 hover:text-stone-900"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    );
  }

  if (billing.cancel_at_period_end) {
    return (
      <div
        className="bg-amber-50 border-b border-amber-200 px-6 py-2.5"
        data-testid="trial-banner-canceling"
      >
        <div className="max-w-[1600px] mx-auto flex items-center gap-3 text-sm">
          <Clock className="w-4 h-4 text-amber-700" strokeWidth={1.5} />
          <span className="text-amber-900">
            Your subscription will end at the period end. Reactivate any time.
          </span>
          <Link
            to="/profile"
            className="ml-auto bg-amber-700 hover:bg-amber-800 text-white text-xs font-semibold uppercase tracking-wider px-4 py-1.5 rounded-full transition-colors"
            data-testid="trial-banner-reactivate"
          >
            Reactivate
          </Link>
        </div>
      </div>
    );
  }

  return null;
}
